import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  TrendingUp, TrendingDown, Building2, Users, Calendar, 
  DollarSign, CheckCircle, Clock, XCircle, ArrowUpRight,
  Activity, Eye, MousePointer
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);
  const [analytics, setAnalytics] = useState({
    dailyVisits: [],
    topPages: [],
    conversionRate: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch all data
      const [facilitiesRes, usersRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/facilities`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true })
      ]);

      const facilities = facilitiesRes.data;
      const users = usersRes.data;
      const bookings = bookingsRes.data;

      // Calculate stats
      const confirmedBookings = bookings.filter(b => b.payment_status === 'CONFIRMED');
      const pendingBookings = bookings.filter(b => b.payment_status === 'PENDING_PAYMENT');
      const todayBookings = bookings.filter(b => {
        const bookingDate = new Date(b.date);
        const today = new Date();
        return bookingDate.toDateString() === today.toDateString();
      });

      const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (b.brut_tutar || 0), 0);
      const totalCommission = confirmedBookings.reduce((sum, b) => sum + (b.komisyon_tutar || 0), 0);

      // Recent bookings (last 5)
      const recent = bookings
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setStats({
        totalFacilities: facilities.length,
        activeFacilities: facilities.filter(f => f.is_partner).length,
        totalUsers: users.length,
        operators: users.filter(u => u.role === 'operator').length,
        totalBookings: bookings.length,
        confirmedBookings: confirmedBookings.length,
        pendingBookings: pendingBookings.length,
        todayBookings: todayBookings.length,
        totalRevenue,
        totalCommission,
      });

      // Calculate analytics
      const last7Days = [...Array(7)].map((_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();
      
      const dailyVisits = last7Days.map(date => {
        const dayBookings = bookings.filter(b => b.date === date);
        return {
          date,
          bookings: dayBookings.length,
          revenue: dayBookings.filter(b => b.payment_status === 'CONFIRMED').reduce((sum, b) => sum + (b.brut_tutar || 0), 0)
        };
      });
      
      // Conversion rate (confirmed bookings / total bookings)
      const conversionRate = bookings.length > 0 
        ? ((confirmedBookings.length / bookings.length) * 100).toFixed(1)
        : 0;
      
      setAnalytics({
        dailyVisits,
        conversionRate
      });

      setRecentBookings(recent);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard data error:', error);
      toast.error('Veriler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Toplam Tesis',
      value: stats.totalFacilities,
      subtitle: `${stats.activeFacilities} Aktif Partner`,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Toplam Kullanıcı',
      value: stats.totalUsers,
      subtitle: `${stats.operators} Operatör`,
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Toplam Rezervasyon',
      value: stats.totalBookings,
      subtitle: `${stats.todayBookings} Bugün`,
      icon: Calendar,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Toplam Gelir',
      value: `${stats.totalRevenue.toLocaleString('tr-TR')} ₺`,
      subtitle: `${stats.totalCommission.toLocaleString('tr-TR')} ₺ Komisyon`,
      icon: DollarSign,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      title: 'Onaylı Rezervasyonlar',
      value: stats.confirmedBookings,
      subtitle: 'Ödeme tamamlandı',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Bekleyen Ödemeler',
      value: stats.pendingBookings,
      subtitle: 'Ödeme bekleniyor',
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      CONFIRMED: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
      PENDING_PAYMENT: { label: 'Ödeme Bekliyor', color: 'bg-orange-100 text-orange-700' },
      CANCELLED: { label: 'İptal', color: 'bg-red-100 text-red-700' },
      EXPIRED: { label: 'Süresi Doldu', color: 'bg-slate-100 text-slate-700' }
    };
    const config = statusConfig[status] || statusConfig.PENDING_PAYMENT;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.subtitle}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Activity Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Son 7 Gün Aktivitesi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.dailyVisits.map((day, index) => {
                const maxBookings = Math.max(...analytics.dailyVisits.map(d => d.bookings), 1);
                const percentage = (day.bookings / maxBookings) * 100;
                
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 w-24">
                        {format(new Date(day.date), 'd MMM', { locale: tr })}
                      </span>
                      <div className="flex-1 mx-4">
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div 
                            className="bg-emerald-600 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-4 text-right">
                        <span className="font-semibold w-16">{day.bookings} rez.</span>
                        <span className="text-emerald-700 font-semibold w-20">
                          {day.revenue.toLocaleString('tr-TR')} ₺
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MousePointer className="w-5 h-5" />
              Performans Metrikleri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-3xl font-bold text-emerald-700">{analytics.conversionRate}%</p>
              <p className="text-xs text-emerald-600 mt-1">Dönüşüm Oranı</p>
              <p className="text-xs text-slate-500 mt-1">Onaylı / Toplam Rezervasyon</p>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-700">{stats?.todayBookings || 0}</p>
              <p className="text-xs text-blue-600 mt-1">Bugünkü Rezervasyonlar</p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-700">{stats?.activeFacilities || 0}</p>
              <p className="text-xs text-purple-600 mt-1">Aktif Partner Tesisler</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Bookings */}
      <Card>
        <CardHeader>
          <CardTitle>Son Rezervasyonlar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentBookings.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Henüz rezervasyon yok</p>
            ) : (
              recentBookings.map((booking) => (
                <div 
                  key={booking.booking_id} 
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{booking.facility_name}</p>
                    <p className="text-sm text-slate-600">
                      {booking.user_name} • {booking.date} • {booking.start_time}-{booking.end_time}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-semibold text-slate-900">{booking.brut_tutar} ₺</p>
                    {getStatusBadge(booking.payment_status)}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboardPage;
